import { useEffect } from 'react';
import { ScriptLoader } from './ScriptLoader';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { COOKIE_PATTERNS } from '@/lib/cookie-utils';

/**
 * Third-party script configurations
 * Add your actual script IDs and configurations here
 */
const SCRIPTS = {
  // Analytics Scripts
  analytics: [
    {
      id: 'google-analytics-gtag',
      src: 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX', // Replace with actual GA ID
      async: true,
      enabled: false, // Set to true when you have a real GA ID
      cookiePatterns: {
        cookies: COOKIE_PATTERNS.googleAnalytics.cookies,
        localStorage: COOKIE_PATTERNS.googleAnalytics.localStorage,
        sessionStorage: COOKIE_PATTERNS.googleAnalytics.sessionStorage,
      },
    },
    {
      id: 'google-analytics-config',
      innerHTML: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-XXXXXXXXXX', {
          anonymize_ip: true,
          cookie_flags: 'SameSite=None;Secure'
        });
      `,
      enabled: false, // Set to true when you have a real GA ID
      cookiePatterns: {
        cookies: COOKIE_PATTERNS.googleAnalytics.cookies,
        localStorage: COOKIE_PATTERNS.googleAnalytics.localStorage,
        sessionStorage: COOKIE_PATTERNS.googleAnalytics.sessionStorage,
      },
    },
    {
      id: 'mixpanel',
      innerHTML: `
        (function(f,b){if(!b.__SV){var e,g,i,h;window.mixpanel=b;b._i=[];b.init=function(e,f,c){function g(a,d){var b=d.split(".");2==b.length&&(a=a[b[0]],d=b[1]);a[d]=function(){a.push([d].concat(Array.prototype.slice.call(arguments,0)))}}var a=b;"undefined"!==typeof c?a=b[c]=[]:c="mixpanel";a.people=a.people||[];a.toString=function(a){var d="mixpanel";"mixpanel"!==c&&(d+="."+c);a||(d+=" (stub)");return d};a.people.toString=function(){return a.toString(1)+".people (stub)"};i="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
        for(h=0;h<i.length;h++)g(a,i[h]);var j="set set_once union unset remove delete".split(" ");a.get_group=function(){function b(c){d[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));a.push([e,call2])}}for(var d={},e=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<j.length;c++)b(j[c]);return d};b._i.push([e,f,c])};b.__SV=1.2;e=f.createElement("script");e.type="text/javascript";e.async=!0;e.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?MIXPANEL_CUSTOM_LIB_URL:"file:"===f.location.protocol&&"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js".match(/^\\/\\//)?"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js":"//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";g=f.getElementsByTagName("script")[0];g.parentNode.insertBefore(e,g)}})(document,window.mixpanel||[]);
      `,
      enabled: false, // Set to true when you have Mixpanel configured
      cookiePatterns: {
        cookies: COOKIE_PATTERNS.mixpanel.cookies,
        localStorage: COOKIE_PATTERNS.mixpanel.localStorage,
        sessionStorage: COOKIE_PATTERNS.mixpanel.sessionStorage,
      },
    },
  ],

  // Marketing Scripts
  marketing: [
    {
      id: 'facebook-pixel',
      innerHTML: `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', 'YOUR_PIXEL_ID'); // Replace with actual Pixel ID
        fbq('track', 'PageView');
      `,
      enabled: false, // Set to true when you have a real FB Pixel ID
      cookiePatterns: {
        cookies: COOKIE_PATTERNS.facebook.cookies,
        localStorage: COOKIE_PATTERNS.facebook.localStorage,
        sessionStorage: COOKIE_PATTERNS.facebook.sessionStorage,
      },
    },
    {
      id: 'google-ads',
      src: 'https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXX', // Replace with actual ID
      async: true,
      enabled: false, // Set to true when you have Google Ads configured
      cookiePatterns: {
        cookies: COOKIE_PATTERNS.googleAds.cookies,
        localStorage: COOKIE_PATTERNS.googleAds.localStorage,
        sessionStorage: COOKIE_PATTERNS.googleAds.sessionStorage,
      },
    },
    {
      id: 'google-ads-config',
      innerHTML: `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-XXXXXXXXX'); // Replace with actual ID
      `,
      enabled: false, // Set to true when you have Google Ads configured
      cookiePatterns: {
        cookies: COOKIE_PATTERNS.googleAds.cookies,
        localStorage: COOKIE_PATTERNS.googleAds.localStorage,
        sessionStorage: COOKIE_PATTERNS.googleAds.sessionStorage,
      },
    },
  ],

  // Preference Scripts (e.g., personalization, chat widgets)
  preferences: [
    {
      id: 'intercom',
      innerHTML: `
        window.intercomSettings = {
          api_base: "https://api-iam.intercom.io",
          app_id: "YOUR_APP_ID" // Replace with actual Intercom app ID
        };
        (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/YOUR_APP_ID';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
      `,
      enabled: false, // Set to true when you have Intercom configured
      cookiePatterns: {
        cookies: COOKIE_PATTERNS.intercom.cookies,
        localStorage: COOKIE_PATTERNS.intercom.localStorage,
        sessionStorage: COOKIE_PATTERNS.intercom.sessionStorage,
      },
    },
    {
      id: 'hotjar',
      innerHTML: `
        (function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:XXXXXXX,hjsv:6}; // Replace with actual Hotjar ID
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
      `,
      enabled: false, // Set to true when you have Hotjar configured
      cookiePatterns: {
        cookies: COOKIE_PATTERNS.hotjar.cookies,
        localStorage: COOKIE_PATTERNS.hotjar.localStorage,
        sessionStorage: COOKIE_PATTERNS.hotjar.sessionStorage,
      },
    },
  ],
};

/**
 * ScriptManager - Central management for all third-party scripts
 * 
 * Features:
 * - Loads scripts based on user consent
 * - Respects Do Not Track settings
 * - Dynamically adds/removes scripts when consent changes
 * - Provides logging for debugging
 */
export function ScriptManager() {
  const { consent, hasDoNotTrack } = useCookieConsent();

  // Listen for consent changes
  useEffect(() => {
    const handleConsentChange = (_event: Event) => {
      // Consent changed - scripts will re-evaluate
    };

    const handleConsentWithdrawn = () => {
      // Consent withdrawn - scripts will be removed
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);
    window.addEventListener('cookieConsentWithdrawn', handleConsentWithdrawn);

    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
      window.removeEventListener('cookieConsentWithdrawn', handleConsentWithdrawn);
    };
  }, [consent, hasDoNotTrack]);

  // Render all configured scripts
  return (
    <>
      {/* Analytics Scripts */}
      {SCRIPTS.analytics.map(script => (
        <ScriptLoader
          key={script.id}
          category="analytics"
          script={script}
          enabled={script.enabled}
        />
      ))}

      {/* Marketing Scripts */}
      {SCRIPTS.marketing.map(script => (
        <ScriptLoader
          key={script.id}
          category="marketing"
          script={script}
          enabled={script.enabled}
        />
      ))}

      {/* Preference Scripts */}
      {SCRIPTS.preferences.map(script => (
        <ScriptLoader
          key={script.id}
          category="preferences"
          script={script}
          enabled={script.enabled}
        />
      ))}
    </>
  );
}

/**
 * Hook to track analytics events conditionally
 * Only tracks if analytics consent is given
 */
export function useAnalytics() {
  const { consent, hasDoNotTrack } = useCookieConsent();
  
  const track = (eventName: string, properties?: Record<string, any>) => {
    // Only track if we have analytics consent and DNT is not enabled
    if (!consent.analytics || hasDoNotTrack) {
      return;
    }

    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', eventName, properties);
    }

    if (typeof (window as any).mixpanel !== 'undefined') {
      (window as any).mixpanel.track(eventName, properties);
    }
  };

  const identify = (userId: string, traits?: Record<string, any>) => {
    // Only identify if we have analytics consent and DNT is not enabled
    if (!consent.analytics || hasDoNotTrack) {
      return;
    }

    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('config', 'G-XXXXXXXXXX', {
        user_id: userId,
        ...traits,
      });
    }

    if (typeof (window as any).mixpanel !== 'undefined') {
      (window as any).mixpanel.identify(userId);
      if (traits) {
        (window as any).mixpanel.people.set(traits);
      }
    }
  };

  return {
    track,
    identify,
    isEnabled: consent.analytics && !hasDoNotTrack,
  };
}

/**
 * Hook to track marketing events conditionally
 * Only tracks if marketing consent is given
 */
export function useMarketing() {
  const { consent, hasDoNotTrack } = useCookieConsent();
  
  const trackConversion = (conversionId: string, value?: number) => {
    // Only track if we have marketing consent and DNT is not enabled
    if (!consent.marketing || hasDoNotTrack) {
      return;
    }

    if (typeof (window as any).fbq !== 'undefined') {
      (window as any).fbq('track', 'Purchase', {
        value: value || 0,
        currency: 'USD',
        conversion_id: conversionId,
      });
    }

    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'conversion', {
        send_to: `AW-XXXXXXXXX/${conversionId}`,
        value: value || 0,
        currency: 'USD',
      });
    }
  };

  return {
    trackConversion,
    isEnabled: consent.marketing && !hasDoNotTrack,
  };
}